import { useTheme } from '@emotion/react'
import React from 'react'
import useBreakpoints from '../../hooks/useBreakpoints'
import { distanceFromNow, generateUrl } from '../../utils'
import { Card, Link, Typography, ShareButton } from '../base-ui'
import { useRouter } from 'next/router'
import config from '../../../config.json'
import { newsDisplayedPubDate } from '../../utils/news'

export default function NewsCard({
    news,
    showAbst,
    shadow,
    type = 'default',
    imageWidth,
    imageHeight,
    borderStyle = 'rounded',
    cardImage = true,
    spacing,
    imageAlt,
    horizentalImageSize = 'normal',
    titleHtmlEl = 'h6',
    titleColor = null,
    uptitleLines = 1,
    titleLines = 2,
    uptitleHtmlEl = 't',
    uptitleColor = 'medGray',
    uptitleStyle,
    contentLines = 2,
    titleFontSize,
    contentStyle,
    className,
    style,
    align,
    target,
    footer = true,
    ...others
}) {
    const theme = useTheme()
    const breakpoint = useBreakpoints()
    const mobileScreen = breakpoint.xs || breakpoint.sm
    const responsiveTitleLines = mobileScreen ? 5 : 3
    const horizentalUptitleLines = mobileScreen ? 2 : 1
    const horizentalSpacing = mobileScreen ? 'sm' : 'md'
    const { locale } = useRouter()

    return (
        <Link
            rel={news?.forward_link ? 'nofollow' : ''}
            target={target}
            href={generateUrl(news, 'news', { asLinkStyle: true })}
            nextHrefAs={generateUrl(news, 'news')}
            nextPrefetch
            style={`
                    display: block;
                    &:hover {
                        .typography {
                            color: ${theme.colors.primary};
                        }
                    }
                    ${style}
                `}
        >
            <Card
                align={align}
                shadow={shadow}
                imgWidth={imageWidth}
                borderStyle={borderStyle}
                imgHeight={type === 'default' ? imageHeight : imageWidth}
                imgAlt={imageAlt || news?.title}
                className={className}
                type={type}
                spacing={type === 'default' ? spacing : horizentalSpacing}
                imgSrc={
                    cardImage &&
                    news?.thumbnail?.file?.hash &&
                    news?.thumbnail?.file?.mime?.indexOf('image/') === 0 &&
                    `/api/filedb/${news.thumbnail.file.hash}.${news.thumbnail.file.ext}`
                }
                videoSrc={
                    cardImage &&
                    news?.thumbnail?.file?.hash &&
                    news?.thumbnail?.file?.mime?.indexOf('video/') === 0 &&
                    `/api/filedb/${news.thumbnail.file.hash}.${news.thumbnail.file.ext}`
                }
                horizentalImageSize={horizentalImageSize}
                style={type === 'horizental' && 'min-height: 9rem;'}
                subtitle={
                    uptitleLines > 0 && (
                        <Typography
                            type={uptitleHtmlEl}
                            color={uptitleColor}
                            lineClamp={
                                type === 'default'
                                    ? uptitleLines
                                    : horizentalUptitleLines
                            }
                            style={uptitleStyle}
                        >
                            {news?.uptitle}
                        </Typography>
                    )
                }
                title={
                    <Typography
                        type={titleHtmlEl || 'h5'}
                        color={titleColor}
                        fontWeight="Bold"
                        fontSize={titleFontSize}
                        lineClamp={titleLines || responsiveTitleLines}
                    >
                        {news?.title}
                    </Typography>
                }
                footer={
                    footer && (
                        <>
                            <span
                                style={{
                                    paddingBottom: type === 'default' ? 4 : 'unset',
                                    unicodeBidi: 'plaintext',
                                }}
                            >
                                {config.settings?.use_time_in_news_cards && newsDisplayedPubDate(news)}
                                {!config.settings?.use_time_in_news_cards && distanceFromNow(newsDisplayedPubDate(news, 'date'), {
                                    locale,
                                })}
                            </span>
                            <div className="mr-auto" dir="ltr">
                                <ShareButton
                                    title={news?.title}
                                    url={generateUrl(news, 'news', {
                                        absolute: true,
                                    })}
                                />
                            </div>
                        </>
                    )
                }
                {...others}
            >
                {showAbst && (
                    <Typography
                        type="p"
                        style={contentStyle}
                        color="medGray"
                        lineClamp={contentLines}
                    >
                        {news?.abst}
                    </Typography>
                )}
            </Card >
        </Link >
    )
}
